import {AfterViewInit, ChangeDetectorRef, Component, Inject, OnInit, PLATFORM_ID, QueryList, ViewChild, ViewChildren,} from '@angular/core';
import {ComboBoxDropdownComponent} from 'ng-design-system';
import {NgbDropdown} from '@ng-bootstrap/ng-bootstrap';
import {AuthService, Country, LanguageService} from '@spartacus/core';
import {CountrySelectorService, CountrySelectorStatus} from '@app/services/country-selector.service';
import {CountrySwitchModalComponent} from './country-switch-modal/country-switch-modal.component';
import {ArrayService} from '@dcp/configurator';
import {isPlatformBrowser} from '@angular/common';
import {distinctUntilChanged, first} from 'rxjs/operators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {GeolocationService} from '@app/services/geolocation.service';

@UntilDestroy()
@Component({
  selector: 'dcp-country-selector',
  templateUrl: './country-selector.component.html',
  styleUrls: ['./country-selector.component.scss'],
})
export class CountrySelectorComponent implements AfterViewInit, OnInit {

  @ViewChildren(ComboBoxDropdownComponent) comboBoxDropdownList: QueryList<ComboBoxDropdownComponent>;
  @ViewChild(NgbDropdown, {static: true}) dropdown: NgbDropdown;
  @ViewChild('countrySwitchModalComponent') countrySwitchModalComponentRef: CountrySwitchModalComponent;

  isoCodesInOriginalOrder: string[] = [];
  countries: Country[] = [];
  changeable = false;
  currentLanguage: string;
  id: number;
  private lastIsocode: string;

  constructor(
    private countrySelectorService: CountrySelectorService,
    private changeDetector: ChangeDetectorRef,
    private authService: AuthService,
    private languageService: LanguageService,
    private geolocationService: GeolocationService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
  }

  get highlighted(): boolean {
    return this.dropdown && this.dropdown.isOpen();
  }

  get isocode(): string {
    return this.countrySelectorService.getCurrentCountry().isocode;
  }

  ngOnInit(): void {
    this.getCountries();
    this.setChangeable();
    this.setupLanguageService();
  }

  ngAfterViewInit(): void {
    this.subscribeToChangeNotifier();
  }

  /**
   * Gets all countries and stores it in global variables
   */
  getCountries(): void {
    this.countrySelectorService.getCountries().pipe(
      distinctUntilChanged(),
      untilDestroyed(this)
    ).subscribe(
      (countries) => {
        this.isoCodesInOriginalOrder = countries.map(a => a.isocode);
        this.countries = countries;
        this.setUserGeoLocatedCountryIfAvailable();
        this.rearrangeCountries(this.countrySelectorService.getCurrentCountry().isocode, this.countries);
      });
  }

  setUserGeoLocatedCountryIfAvailable() {
    this.geolocationService.getGeolocation().pipe(untilDestroyed(this)).subscribe((result) => {
      if (isPlatformBrowser(this.platformId) && result.embargo) {
        window.location.assign('/embargo.html');
      }
      if (!this.countrySelectorService.hasStateFromStorage()) {
        const geoLocatedCountryIso = result.country_code;
        if (geoLocatedCountryIso) {
          const countryIsKnown = this.countries.filter(c => c.isocode.toLowerCase() === geoLocatedCountryIso.toLowerCase());
          if (countryIsKnown.length > 0) {
            this.setCurrentCountry(geoLocatedCountryIso, this.countries);
          }
        }
      }
    });
  }

  onKeydownEvent(event: KeyboardEvent) {
    if (this.dropdown && event.key === 'Enter' || event.key === 'NumpadEnter') {
      this.dropdown.open();
    }
  }

  /**
   * Handles the return value of th countrySwitchModalComponentRef.confirmCountryChange method.
   *
   * @param status string returned by confirmCountryChange method.
   * @param newCountryIsoCode country to set the country picker to.
   */
  handleCountryConfirmation(status: string, newCountryIsoCode: string) {
    if (status !== CountrySelectorStatus.WITHDRAW_COUNTRY_SELECTOR) {
      this.setCurrentCountry(newCountryIsoCode, this.countries);
      this.countrySelectorService.sendChangeNotification(this.isocode);
      if (status !== CountrySelectorStatus.CONFIRM_AND_VISIT_HOMEPAGE) {
        this.countrySelectorService.reloadProduct();
      }
    } else {
      //Make country which was withdrawn last re-selectable
      this.lastIsocode = '';
    }
  }

  /**
   * Does the search if the user opens the country selector and types something in the search field.
   */
  searchCountryInList(newCountryIsoCode: string) {
    this.countrySelectorService.getCountrySwitchStatus(newCountryIsoCode, this.countrySwitchModalComponentRef)
      .pipe(first(), untilDestroyed(this))
      .subscribe(promise => promise
        .then(status => this.handleCountryConfirmation(status, newCountryIsoCode)));
  }

  /**
   * Sets the Country in the countrySelectorService.
   *
   * @param isocode of the country to set.
   * @param countries List of countries to retrieve the Country object containing the isocode from.
   */
  setCurrentCountry(isocode: string, countries: Country[]): void {
    countries = ArrayService.sortObjectsArrayByStringArray(
      countries, this.isoCodesInOriginalOrder, 'isocode',
    );
    const chosenCountryIndex: number = countries.findIndex((country) => country.isocode.toLocaleLowerCase() === isocode?.toLowerCase());
    this.countrySelectorService.setCurrentCountry(countries[chosenCountryIndex]);
    this.countries = ArrayService.moveArrayItem(countries, chosenCountryIndex, 0);
    this.changeDetector.detectChanges();
  }

  rearrangeCountries(isocode: string, countries: Country[]): void {
    countries = ArrayService.sortObjectsArrayByStringArray(
      countries, this.isoCodesInOriginalOrder, 'isocode',
    );
    const chosenCountryIndex: number = countries.findIndex((country) => country.isocode.toLocaleLowerCase() === isocode?.toLowerCase());
    this.countries = ArrayService.moveArrayItem(countries, chosenCountryIndex, 0);
    this.changeDetector.detectChanges();
  }


  /**
   * Subscribes to the message service to get notified when another
   * country selector component currently displayed changes the country
   */
  subscribeToChangeNotifier() {
    this.countrySelectorService.getChangeNotification().pipe(untilDestroyed(this)).subscribe(message => {
      if (message && this.isocode !== message.isoCode) {
        if (this.countries?.length) {
          this.setCurrentCountry(message.isoCode, this.countries);
        }
      } else {
        this.changeDetector.detectChanges();
      }
    });
  }

  /**
   * Sets a value and closes the dropdown.
   *
   * @param value isocode Chosen in the Country selector dropdown
   */
  valueChangeHandler(value: string[]) {
    if (value.length > 0 && value[0] !== this.lastIsocode) {
      // save lastIsocode, so this method is not executed again even though the event is triggered again.
      this.lastIsocode = value[0];
      this.searchCountryInList(value[0]);
    }
    if (this.dropdown) {
      this.dropdown.close();
    }
  }

  dropdownOpenChangeHandler(didOpen: boolean) {
    setTimeout(() => {
      if (this.comboBoxDropdownList?.first) {
        if (didOpen) {
          this.comboBoxDropdownList.first.setFocusState(true);
        } else {
          this.comboBoxDropdownList.first.resetSearch();
          this.changeDetector.detectChanges();
        }
      }
    });
  }

  private setupLanguageService() {
    this.languageService.getActive().pipe(
      distinctUntilChanged(),
      untilDestroyed(this)
    ).subscribe(value => {
      this.currentLanguage = value.toUpperCase();
    });
  }

  /**
   * checks if the user is logged in and set the appropriate values.
   */
  private setChangeable() {
    this.authService.isUserLoggedIn().pipe(
      distinctUntilChanged(),
      untilDestroyed(this)
    ).subscribe(
      (isLoggedIn) => {
        this.changeable = !isLoggedIn;
        this.changeDetector.detectChanges();
      },
      () => {
        this.changeable = true;
        this.changeDetector.detectChanges();
      });
  }
}
